/**
 * Vanity Assembly Constants
 * Shared between cloud functions and web app
 */

// Conversion constant: 1 inch = 0.0254 meters
const INCHES_TO_METERS = 0.0254;

/**
 * Vanity style configurations
 * Defines textures and models for different vanity styles
 */
/**
 * Per-style vanity configuration.
 *
 * `modelQuery` terms are matched against a product's name / description /
 * tags, so they only mean something when the CATALOG carries the vocabulary.
 * `traditional.faucet` works because `widespread` is a real mount tag (7 of 22
 * sink faucets). `modern` and `transitional` used to search for the literal
 * adjectives "modern" and "classic", which appear on no product, so both fell
 * through to an unfiltered random pick — they now say so by carrying no query
 * at all (#1569).
 *
 * Unlike the shower and tub-faucet cases in that issue, this one COULD be made
 * real: the sink-faucet catalog does distinguish `widespread` (7),
 * `single-handle` (6), `tall-single-handle` (4) and `wall-mount` (4). Mapping
 * those to styles is a design decision, not a code fix, which is why it is not
 * made here.
 */
const VANITY_STYLE_CONFIG = {
  traditional: {
    cabinet: {
      textureQuery: ['wood', 'oak', 'cherry'],
      fallbackColor: '#8B4513',
    },
    countertop: {
      textureQuery: ['marble', 'granite'],
      fallbackColor: '#E8E8E8',
    },
    sink: {
      modelQuery: ['oval', 'undermount'],
      fallbackType: 'sink',
      fallbackSinkType: 'undermount',
    },
    faucet: {
      modelQuery: ['widespread', 'two-handle'],
      fallbackType: 'faucet',
    },
    mirror: { modelQuery: ['framed', 'rectangular'], fallbackType: 'mirror' },
    light: { modelQuery: ['sconce', 'shade'], fallbackType: 'wall-light' },
  },
  modern: {
    cabinet: {
      textureQuery: ['matte', 'flat', 'gray'],
      fallbackColor: '#4A4A4A',
    },
    countertop: { textureQuery: ['quartz', 'solid'], fallbackColor: '#F5F5F5' },
    sink: {
      modelQuery: ['vessel', 'rectangular'],
      fallbackType: 'sink',
      fallbackSinkType: 'vessel',
    },
    faucet: { fallbackType: 'faucet' },
    mirror: {
      modelQuery: ['frameless', 'LED'],
      fallbackType: 'mirror',
      allowProcedural: true, // Modern style can use procedural mirror
    },
    light: {
      modelQuery: ['LED', 'bar', 'linear'],
      fallbackType: 'overhead-wall-light',
    },
  },
  transitional: {
    cabinet: {
      textureQuery: ['shaker', 'white', 'gray'],
      fallbackColor: '#FFFFFF',
    },
    countertop: { textureQuery: ['quartz', 'white'], fallbackColor: '#FAFAFA' },
    sink: {
      modelQuery: ['undermount', 'rectangular'],
      fallbackType: 'sink',
      fallbackSinkType: 'undermount',
    },
    faucet: { fallbackType: 'faucet' },
    mirror: { modelQuery: ['simple', 'frame'], fallbackType: 'mirror' },
    light: { modelQuery: ['sconce', 'modern'], fallbackType: 'wall-light' },
  },
};

/**
 * Vanity dimension defaults (in meters)
 */
const VANITY_DIMENSIONS = {
  singleSink: {
    width: 0.914, // 36 inches
    depth: 0.533, // 21 inches
    height: 0.813, // 32 inches
  },
  doubleSink: {
    width: 1.524, // 60 inches
    depth: 0.533, // 21 inches
    height: 0.813, // 32 inches
  },
  countertop: {
    thickness: 0.038, // 1.5 inches
    overhang: 0.025, // 1 inch front and sides
  },
  sink: {
    width: 0.6096, // 24 inches
    depth: 0.457, // 18 inches
    height: 0.2, // 8 inch basin
  },
  mirror: {
    width: 0.762, // 30 inches
    height: 0.914, // 36 inches
    depth: 0.004, // ~4mm realistic mirror thickness
    heightAboveCounter: 0.254, // 10 inches gap (clears wall faucets)
  },
  sconce: {
    width: 0.127, // 5 inches
    height: 0.381, // 15 inches
    depth: 0.127, // 5 inches
    offsetFromMirror: 0.076, // 3 inches from mirror edge
  },
  // Fallback dimensions when no catalog model is available. Kept in inches
  // and converted at construction so every caller resolves to the same
  // meters value.
  overheadLight: {
    width: 24 * INCHES_TO_METERS,
    height: 6 * INCHES_TO_METERS,
    depth: 4 * INCHES_TO_METERS,
  },
  ceilingLight: {
    width: 12 * INCHES_TO_METERS,
    height: 6 * INCHES_TO_METERS,
    depth: 12 * INCHES_TO_METERS,
  },
  lightBar: {
    height: 0.1, // 4 inches
    depth: 0.1, // 4 inches
    heightAboveMirror: 0.05, // 2 inches
  },
};

/**
 * Counter left around a sink's CUTOUT. Width margin is the countertop
 * reveal each side; depth margins keep a basin off the backsplash and the
 * front edge. A vessel sits ON the counter and may overhang the front, so
 * it is judged on a back gap plus a front margin instead of a symmetric
 * reveal. Lives here rather than in sinkFit.js so WALL_HUNG_SINK_MAX can
 * be derived from it — the two were independent numbers and drifted.
 */
/**
 * The base cabinet a BATHROOM sink bay reserves, when nothing measured says
 * otherwise (`cabinet-runs/profiles.js` → the vanity profile).
 *
 * 30", and it is a decision rather than a calculation. The rule that produces
 * it is the same one `sinkBaseWidthFor` applies — the narrowest standard base
 * (24 / 30 / 36) that clears the basin by `SINK_BASE_SIDE_ALLOWANCE_IN` a
 * side — read against this subsystem's own 24" basin: 24 + 2×3 = 30. It is
 * stated here rather than derived at load so `profiles.js` stays a pure
 * re-export and `bays.js` can read a profile without a require cycle.
 *
 * It is NOT the kitchen's 36". That number is deliberate headroom so a real
 * 32-35" catalog sink still fits the base the generator reserved
 * (`KITCHEN_SINK_BASE_WIDTH`), and applying the allowance rule to the
 * kitchen's own 27" basin would return 30" — so the kitchen's answer cannot
 * be derived either, and the two profiles disagree on purpose.
 */
const VANITY_SINK_BASE_WIDTH = 30 * INCHES_TO_METERS;

const SINK_FIT_MARGINS = {
  width: 3 * INCHES_TO_METERS,
  depth: 1.25 * INCHES_TO_METERS,
  vesselBackGap: 1 * INCHES_TO_METERS,
  vesselFront: 2 * INCHES_TO_METERS,
};

/**
 * The smallest sink in the catalog, by CUTOUT — the hole that has to fit
 * the countertop. Measured over `unifiedObjects` on dev, 2026-09-01:
 * `kohler-8188` (undermount), 12.57" x 12.81". Re-measure by listing every
 * `type == 'sink'` doc's `sink-cutout.metadata.bounds` and taking the
 * minimum; ignore records with no `sinkType`, which is how a broken
 * 3.33" x 0.93" entry currently reads.
 *
 * UNDERMOUNT deliberately, not the global minimum. A vessel needs only
 * 14.45" and a pedestal 15.02", but undermount is the realisation both
 * pipelines default to — a vessel is chosen only when scan geometry
 * demands it, and a box that shallow is better served by the wall-hung
 * unit than by a bowl perched on a 15" counter.
 */
const SMALLEST_CATALOG_SINK_CUTOUT = {
  width: 12.57 * INCHES_TO_METERS,
  depth: 12.81 * INCHES_TO_METERS,
};

/**
 * Largest cabinet that is realised as a one-piece wall-hung sink instead
 * of a cabinet + countertop + basin vanity: anything smaller can only be
 * furnished with the procedural wall-hung unit. Compared with `<` on
 * either axis. See reference-docs/bathroom-fixtures/vanity-assembly.md.
 *
 * DERIVED, so it cannot drift from the fit rule again. It used to be a
 * hand-written 14.5" ("cutout + 3"") against a fit rule that actually
 * wants cutout + 2 x 1.25", and against a catalog whose shallowest cutout
 * is 12.81" rather than the assumed 11.5". Boxes in the resulting 0.8"
 * band cleared this gate with no sink that could fit them.
 *
 * Width keeps its 6" reveal (3" a side, `SINK_FIT_MARGINS.width`) over the
 * smallest basin's BODY, which is what a cabinet face needs; the fit rule
 * only bounds cutout width by the box, so deriving width the same way as
 * depth would drop this gate to 12.57" and let a 13"-wide cabinet be built
 * as a vanity with no reveal at all.
 */
const WALL_HUNG_SINK_MAX = {
  width: 19 * INCHES_TO_METERS,
  depth: SMALLEST_CATALOG_SINK_CUTOUT.depth + 2 * SINK_FIT_MARGINS.depth,
};

/**
 * What a vanity is built at on the SCAN path when nothing has measured it.
 *
 * The growth rule exists because RoomPlan under-measures a carcass (it sees the
 * door faces) and a basin then does not fit — `Anchorage 2 / Scan 2` delivered
 * 13.4" against a real 21", and a 15.6" catalog sink landed in it with the
 * faucet 3.2" inside the wall. Growing toward the catalog's 21" fixed that and
 * overshot in the other direction: 21" is what a vanity is when someone CHOOSES
 * one, not what a scanned bathroom contains. 89823100's is the case — with its
 * toilet correctly parented (nothing in front of the box any more) the search
 * grades 19.2" as free and builds a cabinet nearly twice the depth of the real
 * one, on no evidence but the absence of an obstruction.
 *
 * So the unmeasured default is the SHALLOWEST depth that still holds a basin we
 * carry, rather than the deepest the room permits: `WALL_HUNG_SINK_MAX.depth`,
 * one margin above the smallest catalog cutout. DERIVED from the fit rule on
 * purpose — a hand-picked "shallow" number would drift from it exactly as the
 * old hand-written 14.5" drifted, and anything below it cannot hold a catalog
 * basin at all, which is the wall-hung unit's job and not a cabinet's.
 *
 * It is a FLOOR, not a cap: `planVanityBox` never shrinks, so a vanity the scan
 * measured deeper than this keeps its own depth. And it is a placeholder for a
 * measurement — the LiDAR `fixture-face` pass reads a carcass front directly
 * (−5.4" against RoomPlan's box on 89823100's other vanity) and refines it.
 */
const SHALLOW_VANITY_DEPTH = WALL_HUNG_SINK_MAX.depth;

/**
 * How far the countertop reaches past the CARCASS front on the scan path — the
 * basin overhang.
 *
 * RoomPlan measures the vanity's outer ENVELOPE, and that is the counter, not
 * the cabinet: the `fixture-face` pass put the door face of 89823100's other
 * vanity 5.4" behind its box front. One number was doing two jobs, so the
 * cabinet inherited the counter's depth and the strip of counter in front of
 * the bowl was built as carcass.
 *
 * The split is the fit rule's own, so no new figure is invented. The counter
 * is cutout + a margin each side; the bowl must hang inside the carcass, so
 * the carcass spans the cutout AND the back margin, and the FRONT margin — the
 * counter ahead of the bowl, which needs nothing under it — is what overhangs:
 *
 *     counter  = cutout + 2 x margin   (15.31")
 *     carcass  = counter - margin      (14.06")
 *     overhang = margin                ( 1.25")
 *
 * A deeper overhang is a real form (a console basin mostly in front of a
 * shallow cabinet) but it is NOT derivable from the fit rule — past the front
 * margin the bowl itself starts to hang unsupported — so it would be a chosen
 * number, and this is not it.
 */
const VANITY_BASIN_OVERHANG = SINK_FIT_MARGINS.depth;

/**
 * How far a counter may overhang the FRONT of its carcass, at most.
 *
 * A CHOSEN number — the one the note above declined to invent — ruled at 6"
 * (user, 2026-09-25: "the sink can extend beyond the cabinet"). The overhang
 * above is what the fit rule derives for an ordinary vanity; this is the
 * budget a SHALLOW carcass may spend to carry a bowl it could not otherwise
 * hold, which is the console-basin form: a counter and bowl standing mostly in
 * front of a narrow cabinet.
 *
 * What it buys, measured over dev's 339 vanities: of the five whose carcass is
 * too shallow for the smallest catalog bowl at the derived 1.25", four come
 * inside 6" — 13.7" and 13.6" need under 2", and 10.6" and 10.0" need 4.7" and
 * 5.3". The fifth measures 7.0" and would need 8.3"; past this budget the
 * answer stays the one-piece wall-hung unit, which is the form a 7" box really
 * is. So the cap is where the population stops rather than where the physics
 * does — it is a judgement about how much bowl may hang unsupported, and 6"
 * is the ruling.
 */
const VANITY_BASIN_OVERHANG_MAX = 6 * INCHES_TO_METERS;

/** Proportions of the procedural wall-hung unit, shared with its renderer. */
const WALL_HUNG_SINK = {
  height: 7 * INCHES_TO_METERS, // rim to the bottom of the shroud
  // Widest unit we render. The pipeline may hand us a body grown wide
  // but blocked in depth (36" x 12"); a wall-hung basin is at most the
  // 24" basin clearance, centred where the body was.
  maxWidth: 24 * INCHES_TO_METERS,
  // Deepest unit we render, and the twin of `maxWidth` above. Nothing capped
  // the depth, so a conversion from a standard vanity inherited its 21" and
  // produced a 24" x 21" basin — nearly as deep as the cabinet it replaced,
  // which is not the form. A wall-hung unit is a shallow one; 15" is a real
  // console-basin depth and leaves the `deckFraction` faucet deck at ~4.2"
  // instead of the 5.9" a 21" body gave it.
  maxDepth: 15 * INCHES_TO_METERS,
  deckMinDepth: 2 * INCHES_TO_METERS, // faucet deck front-to-back, at least
  deckFraction: 0.28, // ...or this share of the unit depth, whichever is larger
};

module.exports = {
  INCHES_TO_METERS,
  VANITY_STYLE_CONFIG,
  VANITY_DIMENSIONS,
  VANITY_SINK_BASE_WIDTH,
  SINK_FIT_MARGINS,
  SHALLOW_VANITY_DEPTH,
  VANITY_BASIN_OVERHANG,
  VANITY_BASIN_OVERHANG_MAX,
  SMALLEST_CATALOG_SINK_CUTOUT,
  WALL_HUNG_SINK_MAX,
  WALL_HUNG_SINK,
};
